import { alpha, createTheme } from "@mui/material/styles";
import type { TenantThemeConfig } from "@/config/themes/types";

declare module "@mui/material/styles" {
  interface Palette {
    surfaceAlt: Palette["primary"];
    hover: {
      primary: string;
      secondary: string;
      tertiary: string;
    };
  }
  interface PaletteOptions {
    surfaceAlt?: PaletteOptions["primary"];
    hover?: {
      primary?: string;
      secondary?: string;
      tertiary?: string;
    };
  }
}

export function createMuiThemeFromConfig(config: TenantThemeConfig) {
  const { colors, typography } = config;

  return createTheme({
    palette: {
      mode: config.mode,
      primary: {
        main: colors.primary,
        dark: colors.primaryDark,
        light: colors.primaryLight,
        contrastText: colors.textOnPrimary,
      },
      secondary: {
        main: colors.secondary,
      },
      error: {
        main: colors.error,
        light: alpha(colors.error, 0.12),
      },
      success: {
        main: colors.success,
        light: alpha(colors.success, 0.12),
      },
      warning: { main: colors.warning },
      info: { main: colors.info },
      background: {
        default: colors.background,
        paper: colors.surface,
      },
      text: {
        primary: colors.textPrimary,
        secondary: colors.textSecondary,
        disabled: colors.textDisabled,
      },
      divider: colors.border,
      surfaceAlt: {
        main: colors.surfaceAlt,
      },
      hover: {
        primary: colors.hoverPrimary,
        secondary: colors.hoverSecondary,
        tertiary: colors.hoverTertiary,
      },
    },
    typography: {
      fontFamily: typography.fontFamily,
    },
    shape: { borderRadius: 10 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colors.background,
            color: colors.textPrimary,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.06)",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: colors.surfaceHover || colors.hoverSecondary,
            },
            "&.Mui-selected": {
              backgroundColor: colors.hoverSecondary,
              "&:hover": {
                backgroundColor: colors.hoverTertiary,
              },
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: colors.border,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: colors.textSecondary,
            "&:hover": {
              backgroundColor: colors.hoverSecondary,
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.surface,
            borderColor: colors.border,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          // MUI leaves this as `inherit`, which resolves to the browser's `middle` for a `td`.
          // That centres every cell against the tallest one in its row, so on a table carrying a
          // case subject beside a full chat transcript the short values float in the middle of a
          // very tall row and stop lining up with the rows above and below. Top-aligning makes
          // the first line of every cell share a baseline, which is what makes such a table
          // scannable. Set on `root` rather than `body` so header cells move with their columns.
          root: {
            verticalAlign: "top",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colors.surfaceRaised,
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
          },
        },
      },
    },
  });
}
